import { cityById, cityByName, type PortugueseCity } from '@powerful/config/geo';
import { useEffect, useState } from 'react';
import { getSupabaseClient } from '../supabase';
import { deviceLocationProvider, parseProfilePoint, requestApproximatePosition, roundOrigin, type LocationProvider, type SearchOrigin } from './geolocation';

export const LOCATION_SESSION_KEY = 'powerful.discovery.location.v1';
export type ConsumerLocation = { source: 'city' | 'device' | 'profile'; origin: SearchOrigin; city: PortugueseCity | null };
export type LocationState = 'loading' | 'ready' | 'requesting' | 'denied' | 'unavailable';
export interface ProfileLocation { city: string | null; geo: unknown }

export async function loadProfileLocation(): Promise<ProfileLocation | null> {
  try {
    const client = getSupabaseClient();
    // O AuthProvider já resolveu a sessão nesta rota. getUser() faria outro RTT antes de ler o
    // perfil; a RLS continua a validar o JWT no pedido de dados.
    const { data: auth } = await client.auth.getSession();
    if (!auth.session?.user) return null;
    const { data, error } = await client.from('consumer_profiles').select('city,geo').eq('user_id', auth.session.user.id).maybeSingle();
    return error ? null : data;
  } catch { return null; }
}

export function parseSavedLocation(raw: string | null): ConsumerLocation | null {
  if (!raw) return null;
  try {
    const saved: unknown = JSON.parse(raw);
    if (!saved || typeof saved !== 'object' || !('source' in saved)) return null;
    if (saved.source === 'city' && 'cityId' in saved && typeof saved.cityId === 'string') {
      const city = cityById(saved.cityId);
      return city ? { source: 'city', city, origin: { lat: city.lat, lng: city.lng } } : null;
    }
    if (saved.source === 'device' && 'origin' in saved && saved.origin && typeof saved.origin === 'object' && 'lat' in saved.origin && 'lng' in saved.origin && typeof saved.origin.lat === 'number' && typeof saved.origin.lng === 'number') {
      return { source: 'device', city: null, origin: roundOrigin({ lat: saved.origin.lat, lng: saved.origin.lng }) };
    }
  } catch { /* dado de sessão inválido */ }
  return null;
}

export function locationFromProfile(profile: ProfileLocation | null): ConsumerLocation | null {
  if (!profile) return null;
  const city = profile.city ? cityByName(profile.city) ?? null : null;
  const geo = parseProfilePoint(profile.geo);
  if (geo) return { source: 'profile', city, origin: geo };
  return city ? { source: 'profile', city, origin: { lat: city.lat, lng: city.lng } } : null;
}

export interface ConsumerLocationOptions {
  cityId?: string;
  loadProfile?: () => Promise<ProfileLocation | null>;
  provider?: LocationProvider;
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
}

/** Nunca pede permissão no mount. O consumidor pode buscar sem origem. */
export function useConsumerLocation(options: ConsumerLocationOptions = {}) {
  const [location, setLocation] = useState<ConsumerLocation | null>(null);
  const [status, setStatus] = useState<LocationState>('loading');
  const storage = options.storage ?? sessionStorage;

  useEffect(() => {
    let active = true;
    const selectedCity = options.cityId ? cityById(options.cityId) : undefined;
    if (selectedCity) {
      setLocation({ source: 'city', city: selectedCity, origin: { lat: selectedCity.lat, lng: selectedCity.lng } });
      setStatus('ready');
      return;
    }
    let saved: ConsumerLocation | null = null;
    try { saved = parseSavedLocation(storage.getItem(LOCATION_SESSION_KEY)); } catch { /* armazenamento bloqueado */ }
    if (saved) { setLocation(saved); setStatus('ready'); return; }
    void (options.loadProfile ?? loadProfileLocation)().then((profile) => {
      if (!active) return;
      setLocation(locationFromProfile(profile));
      setStatus('ready');
    }).catch(() => { if (active) setStatus('ready'); });
    return () => { active = false; };
  }, [options.cityId, options.loadProfile, storage]);

  const selectCity = (cityId: string): boolean => {
    const city = cityById(cityId);
    if (!city) return false;
    setLocation({ source: 'city', city, origin: { lat: city.lat, lng: city.lng } });
    setStatus('ready');
    try { storage.setItem(LOCATION_SESSION_KEY, JSON.stringify({ source: 'city', cityId })); } catch { /* sessão opcional */ }
    return true;
  };

  const requestDeviceLocation = async (): Promise<boolean> => {
    setStatus('requesting');
    try {
      const origin = await requestApproximatePosition(options.provider ?? deviceLocationProvider());
      setLocation({ source: 'device', city: null, origin });
      setStatus('ready');
      try { storage.setItem(LOCATION_SESSION_KEY, JSON.stringify({ source: 'device', origin })); } catch { /* sessão opcional */ }
      return true;
    } catch (error) {
      setStatus(error && typeof error === 'object' && 'code' in error && error.code === 1 ? 'denied' : 'unavailable');
      return false;
    }
  };

  const clearLocation = () => {
    setLocation(null);
    setStatus('ready');
    try { storage.removeItem(LOCATION_SESSION_KEY); } catch { /* sessão opcional */ }
  };

  return { location, status, selectCity, requestDeviceLocation, clearLocation };
}
